import { incApi } from 'api/inc-api';
import { getClientId } from 'util/client-id';
import { generateProvides, generateProvidesList } from 'util/generateTags';
import {
  ListAccountVerificationsResult,
  ListGuestCheckoutVerificationsResult,
  ListMarketingSMSProgramsResult,
  ListOrderConfirmationsResult,
  ListPasswordlessAuthenticationsResult,
  SMSTotalsByTypeResult,
  SMSType,
} from './sms-reports.type';
import { DateTime } from 'luxon';

const domain = 'incentivio-connector-sms';

const formatDate = inputDate =>
  DateTime.fromISO(inputDate).toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'");

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    getSMSTotalsByType: build.query<
      SMSTotalsByTypeResult,
      {
        startDate: string;
        endDate: string;
        type: SMSType;
      }
    >({
      query: params => ({
        url: `${domain}/clients/${getClientId()}/sms-totals-by-type`,
        params: {
          ...params,
          startDate: formatDate(params.startDate),
          endDate: formatDate(params.endDate),
        },
      }),
      providesTags: generateProvides((_result, _error, arg) => arg, 'SMSTotal'),
    }),

    listMarketingSMSPrograms: build.query<ListMarketingSMSProgramsResult, any>({
      query: params => ({
        url: `${domain}/clients/${getClientId()}/marketing-sms`,
        params: {
          ...params,
          startDate: formatDate(params.startDate),
          endDate: formatDate(params.endDate),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.marketingSmsPrograms,
        element => element.communication,
        'MarketingSMSProgram',
      ),
    }),

    exportMarketingSMSProgramsReport: build.query({
      query: params => {
        return {
          url: `${domain}/clients/${getClientId()}/marketing-sms/download`,
          params: {
            ...params,
            startDate: formatDate(params.startDate),
            endDate: formatDate(params.endDate),
          },
          responseHandler: response => response.text(),
        };
      },
    }),

    listOrderConfirmations: build.query<ListOrderConfirmationsResult, any>({
      query: params => {
        // Destructure and remove storeIds if its value is "*"
        const { storeIds, ...filteredParams } = params;
        const transformedParams = storeIds === '*' ? filteredParams : params;

        return {
          url: `${domain}/clients/${getClientId()}/order-confirmation-sms`,
          params: {
            ...transformedParams,
            startDate: formatDate(params.startDate),
            endDate: formatDate(params.endDate),
          },
        };
      },
      providesTags: generateProvidesList(
        result => result?.orderConfirmations,
        element => element,
        'OrderConfirmationList',
      ),
      transformResponse: res => ({
        orderConfirmations: res,
      }),
    }),

    exportOrderConfirmationsReport: build.query({
      query: params => {
        // Destructure and remove storeIds if its value includes "ALL"
        const { storeIds, ...filteredParams } = params;
        const transformedParams = storeIds.includes('ALL')
          ? filteredParams
          : params;

        return {
          url: `${domain}/clients/${getClientId()}/order-confirmation-sms/download`,
          params: {
            ...transformedParams,
            startDate: formatDate(params.startDate),
            endDate: formatDate(params.endDate),
          },
          responseHandler: response => response.text(),
        };
      },
    }),

    listGuestCheckoutVerifications: build.query<
      ListGuestCheckoutVerificationsResult,
      any
    >({
      query: params => {
        // Destructure and remove storeIds if its value is "*"
        const { storeIds, ...filteredParams } = params;
        const transformedParams = storeIds === '*' ? filteredParams : params;

        return {
          url: `${domain}/clients/${getClientId()}/guest-checkout-verification`,
          params: {
            ...transformedParams,
            startDate: formatDate(params.startDate),
            endDate: formatDate(params.endDate),
          },
        };
      },
      providesTags: generateProvidesList(
        result => result?.guestCheckoutVerifications,
        element => element.locationId,
        'GuestCheckoutVerificationList',
      ),
      transformResponse: res => ({
        guestCheckoutVerifications: res,
      }),
    }),

    exportGuestCheckoutVerificationsReport: build.query({
      query: params => {
        // Destructure and remove storeIds if its value includes "ALL"
        const { storeIds, ...filteredParams } = params;
        const transformedParams = storeIds.includes('ALL')
          ? filteredParams
          : params;

        return {
          url: `${domain}/clients/${getClientId()}/guest-checkout-verification/download`,
          params: {
            ...transformedParams,
            startDate: formatDate(params.startDate),
            endDate: formatDate(params.endDate),
          },
          responseHandler: response => response.text(),
        };
      },
    }),

    listAccountVerifications: build.query<ListAccountVerificationsResult, any>({
      query: params => ({
        url: `${domain}/clients/${getClientId()}/account-verification-sms`,
        params: {
          ...params,
          startDate: formatDate(params.startDate),
          endDate: formatDate(params.endDate),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.accountVerifications,
        element => element,
        'AccountVerificationList',
      ),
      transformResponse: res => ({
        accountVerifications: res,
      }),
    }),

    // Mirrors account-verification (confirmed by the BE, sc-364510). Same
    // request/response shape as the account-verification-sms endpoint.
    listPasswordlessAuthentications: build.query<
      ListPasswordlessAuthenticationsResult,
      any
    >({
      query: params => ({
        url: `${domain}/clients/${getClientId()}/passwordless-auth-sms`,
        params: {
          ...params,
          startDate: formatDate(params.startDate),
          endDate: formatDate(params.endDate),
        },
      }),
      providesTags: generateProvidesList(
        result => result?.passwordlessAuthentications,
        element => element,
        'PasswordlessAuthenticationList',
      ),
      transformResponse: res => ({
        passwordlessAuthentications: res,
      }),
    }),
  }),
});

export const {
  useGetSMSTotalsByTypeQuery,
  useListMarketingSMSProgramsQuery,
  useLazyExportMarketingSMSProgramsReportQuery,
  useListOrderConfirmationsQuery,
  useLazyExportOrderConfirmationsReportQuery,
  useListGuestCheckoutVerificationsQuery,
  useLazyExportGuestCheckoutVerificationsReportQuery,
  useListAccountVerificationsQuery,
  useListPasswordlessAuthenticationsQuery,
} = endpoints;
